import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err: any) {
      console.error("Login error:", err);
      setError("Usuario o contraseña incorrectos.");
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2.5,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 440 }}>
        {/* Brand Header */}
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3.5,
              bgcolor: "#0f172a",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.15)",
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 26, color: "#34d399" }} />
          </Box>
          <Typography variant="h5" fontWeight={900} color="#0f172a" letterSpacing="-0.5px">
            Next Connection
          </Typography>
          <Typography variant="body2" color="#64748b" fontWeight={500} mt={0.5}>
            Panel Central de Administración
          </Typography>
        </Box>

        {/* Login Paper Card */}
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleLogin}
          sx={{
            p: { xs: 3, sm: 4.5 },
            borderRadius: 4,
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
            boxShadow: "0 10px 30px -10px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                Iniciar Sesión
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ingresa con tu cuenta de administrador autorizada
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2.5, fontSize: "0.82rem" }}>
                {error}
              </Alert>
            )}

            <Box>
              <Typography variant="caption" fontWeight={700} sx={{ mb: 0.8, display: "block", color: "#475569" }}>
                CORREO ELECTRÓNICO *
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="admin@nextconnection.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    borderRadius: 2.5,
                    border: "1px solid #e2e8f0",
                    "& fieldset": { border: "none" },
                    "&.Mui-focused": { border: "1.5px solid #0f172a", bgcolor: "#ffffff" },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={700} sx={{ mb: 0.8, display: "block", color: "#475569" }}>
                CONTRASEÑA *
              </Typography>
              <TextField
                fullWidth
                size="small"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword ? <VisibilityOff sx={{ fontSize: "1.1rem" }} /> : <Visibility sx={{ fontSize: "1.1rem" }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    borderRadius: 2.5,
                    border: "1px solid #e2e8f0",
                    "& fieldset": { border: "none" },
                    "&.Mui-focused": { border: "1.5px solid #0f172a", bgcolor: "#ffffff" },
                  },
                }}
              />
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                mt: 1,
                bgcolor: "#0f172a",
                color: "#ffffff",
                py: 1.4,
                borderRadius: 2.5,
                fontWeight: 800,
                fontSize: "0.9rem",
                textTransform: "none",
                "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              {loading ? "Verificando credenciales..." : "Ingresar al Panel"}
            </Button>
          </Stack>
        </Paper>

        {/* Security badge footer */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mt={3}>
          <ShieldOutlinedIcon sx={{ fontSize: 16, color: "#10b981" }} />
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Sesión segura protegida con autenticación Firebase
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};
